import { SubscriptionPackage } from '@bpm-web-app/supreme-api-sdk';
import { useCancelSubscription, useGetMeSubscription, useGetSubscriptionPackage, useGetSubscriptionPackages } from '@bpm-web-app/swr-hooks';
import { convertCreatePackagesToPlanItem, convertSupremePackageToPlanItem, getCurrentPlatformLink, getPlatformName, isCreditsPlan, PlanItem, showExitSurvey, useApiErrorHandler } from '@bpm-web-app/utils';
import classNames from 'classnames';
import Router from 'next/router';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ActionModal } from '../../../shared/action-modal/action-modal';
import { LoadingIcon } from '../../../shared/loading-icon/loading-icon';

import SubscriptionItem from '../subscription-item/subscription-item';
import styles from './cancel-plan.module.css';

export interface AccountPlanCancelProps {
    isCreate?: boolean;
}

export function CancelPlan({ isCreate }: AccountPlanCancelProps) {
    const { packageId } = Router.query;
    const [title, setTitle] = useState('');
    const [showCreateCancelModal, setShowCreateCancelModal] = useState(false);
    const [isLatinoWokraroundCancel, setIsLatinoWorkaroundCancel] = useState(false);
    const [isCancelingSubscription, setIsCancelingSubscription] = useState(false);
    const cancelSubscription = useCancelSubscription();
    const errorHandler = useApiErrorHandler(isCreate);
    const { data: createSubscriptionData } = useGetMeSubscription('create', !isCreate);

    // eslint-disable-next-line @typescript-eslint/dot-notation
    const platform = Router.query['platform'] as unknown as SubscriptionPackage.PlatformEnum;

    const { data: streamData } = useGetSubscriptionPackages(SubscriptionPackage.PlatformEnum.Stream, isCreate);
    const { data: downloadData } = useGetSubscriptionPackages(SubscriptionPackage.PlatformEnum.Supreme, isCreate);
    const { data: createData } = useGetSubscriptionPackages(SubscriptionPackage.PlatformEnum.Create, !isCreate);

    const { data: currentPackageData, error: currentPackageError } = useGetSubscriptionPackage(+packageId);

    // NOTE: This screen is shown when the user is either on premium and or the library is ALL
    const cheaperPlansForPlatform = useMemo<PlanItem[]>(() => {
        let plans: SubscriptionPackage[] = [];

        if (platform === SubscriptionPackage.PlatformEnum.Stream && streamData?.data) {
            plans = [...streamData.data];
        } else if (platform === SubscriptionPackage.PlatformEnum.Supreme && downloadData?.data) {
            plans = [...downloadData.data];
        } else if (platform === SubscriptionPackage.PlatformEnum.Create && createData?.data) {
            plans = [...createData.data];
        }

        const currentPlan = currentPackageData?.data;

        if (!plans.length || !currentPlan) return [];

        let plansToShow = [...plans];

        if (isCreate) {
            plansToShow = plansToShow.filter((plan) => !isCreditsPlan(plan));
        } else {
            plansToShow = plans.filter((plan) => plan.name === SubscriptionPackage.NameEnum.Standard && plan.platform === currentPlan.platform);

            if (currentPlan.library !== SubscriptionPackage.LibraryEnum.All) {
                // NOTE: Show cheaper plans
                plansToShow = plansToShow.filter((plan) => plan.library !== SubscriptionPackage.LibraryEnum.All);
            }
        }

        if (plansToShow.length) {
            const plansTitle = isCreate ? `${getPlatformName(plansToShow[0].platform)} Plans` : `${plansToShow[0].name} ${getPlatformName(plansToShow[0].platform)} Plans`;

            setTitle(plansTitle);

            return plansToShow.map<PlanItem>((plan) =>
                isCreate ? convertCreatePackagesToPlanItem(plan, Number(currentPlan?.amount) || 0, createSubscriptionData?.data?.membership) : convertSupremePackageToPlanItem(plan)
            );
        }

        return [];
    }, [createSubscriptionData, createData?.data, currentPackageData?.data, downloadData?.data, isCreate, platform, streamData?.data]);

    const renderPlanItem = useCallback(
        (plan: PlanItem) => (
            <Fragment key={plan.id}>
                <SubscriptionItem
                    {...plan}
                    ctaText={isCreate ? plan.ctaText : 'Downgrade plan'}
                    variant="card-small"
                    onCtaClick={() => Router.push(getCurrentPlatformLink(`/account/plan/change/${plan.id}`))}
                />
            </Fragment>
        ),
        [isCreate]
    );

    const handleCancelation = useCallback(async () => {
        try {
            setIsCancelingSubscription(true);
            await cancelSubscription(platform);
            Router.replace(getCurrentPlatformLink('/account/plan?cancel=true'), getCurrentPlatformLink('/account/plan'));
            showExitSurvey(platform === SubscriptionPackage.PlatformEnum.Supreme ? 'supreme' : 'create');
        } catch (error: any) {
            errorHandler({ error });
        }
        setIsCancelingSubscription(false);
    }, [cancelSubscription, errorHandler, platform]);

    const handleConfirmCancelPlan = useCallback(async () => {
        if (isCreate) {
            setShowCreateCancelModal(true);
        } else if (platform) {
            await handleCancelation();
        }
    }, [handleCancelation, isCreate, platform]);

    useEffect(() => {
        if (currentPackageError && currentPackageError.status === 403) {
            setIsLatinoWorkaroundCancel(true);
        }
    }, [currentPackageError]);

    const beforeYouGoText = useMemo(() => {
        if (currentPackageData?.data.name === SubscriptionPackage.NameEnum.Premium || currentPackageData?.data.name === SubscriptionPackage.NameEnum.PremiumPlus) {
            return 'Please note that invited playlist collaborators can save the playlist to their own accounts. Once your subscription is updated, no further modifications will be received from collaborators. Removing them prior to your subscription updating is recommended if you would like to restrict access.';
        }
        return null;
    }, [currentPackageData]);

    if (!cheaperPlansForPlatform.length && isLatinoWokraroundCancel === false) {
        return null;
    }

    return (
        <>
            <ActionModal
                headerTitle="Before You Cancel"
                title="You will lose your credits when canceling a subscription."
                subtitle="Don't lose your credits! Explore these options for downgrading your plan. You will have a lower monthly subscription price with continued access to samples and sound packs."
                confirmButtonText="Continue"
                onConfirm={handleCancelation}
                onClose={() => {
                    setShowCreateCancelModal(false);
                }}
                variant="light"
                isOpen={showCreateCancelModal}
            />
            <ActionModal
                headerTitle={<div className={styles['cancel-plan__actions-container__progress-popup-title']}><LoadingIcon />Canceling Subscription</div>}
                subtitle="Your subscription is being canceled."
                hideCancel
                variant="dark"
                isOpen={isCancelingSubscription}
            />
            <div className={styles['cancel-plan']}>
                <h2>Before You Go</h2>
                {beforeYouGoText && <p className={styles['cancel-plan__description']}>{beforeYouGoText}</p>}
                <p className={styles['cancel-plan__description']}>Review additional subscription options and change your plan to meet your current needs.</p>
                {cheaperPlansForPlatform.length ? (
                    <>
                        <h3>{title}</h3>
                        <div className={styles['cancel-plan__plan-item-list']}>{cheaperPlansForPlatform?.map((downloadPlan) => renderPlanItem(downloadPlan))}</div>
                    </>
                ) : null}
                <div className={styles['cancel-plan__actions-container']}>
                    <button
                        type="button"
                        onClick={handleConfirmCancelPlan}
                        disabled={isCancelingSubscription}
                        className={classNames(styles['cancel-plan__actions-container--continue-btn'], 'account__button')}
                        aria-label="Continue to Cancel"
                    >
                        Continue to Cancel
                    </button>
                    <button type="button" onClick={Router.back} disabled={isCancelingSubscription} aria-label="Go back" className={classNames(styles['cancel-plan__actions-container--back-btn'], 'account__button-secondary')}>
                        Back
                    </button>
                </div>
            </div>
        </>
    );
}
